import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

/*
 * TWO MONTHS, NOT TWO WEEKS. Gordon: "in the availability page - show current
 * and next month calendar instead of 2 weeks." The window is anchored to the
 * first of a month and always covers that month and the one after it, so a
 * holiday booked for the 28th of next month is on screen today rather than
 * three "Later" presses away.
 *
 * The server's /availability/effective takes any range up to a year, so the
 * two months come back in one request and are split here by month.
 */

/** The first of the month `iso` falls in. */
const monthStartOf = (iso) => `${iso.slice(0, 7)}-01`;

/** The first of the month `n` months after the one `iso` starts. */
const addMonths = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
};

const MONTHS_SHOWN = 2;

const monthLabel = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
  month: 'long', year: 'numeric', timeZone: 'UTC'
});

/**
 * Two layers, as the contractor thinks about them:
 *   - standing hours, the pattern that holds week to week
 *   - dated exceptions that override it: holiday, an appointment, or extra
 *     cover on a day they would not normally work
 */
export default function ContractorAvailability({ contractorId, token }) {
  // See the note in ContractorDashboard's handleAddComment: an administrator
  // using "View as" holds no contractor token, and "Bearer null" is refused
  // outright rather than falling through to their own staff token.
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]);
  const [confirm, confirmDialog] = useConfirm();

  const blank = DAYS.map((_, i) => ({
    day_of_week: i,
    enabled: i >= 1 && i <= 5,
    start_time: '13:00',
    end_time: '17:00'
  }));

  const [pattern, setPattern] = useState(blank);
  const [exceptions, setExceptions] = useState([]);
  const [preview, setPreview] = useState([]);
  // The calendar window: the first of a month, and it shows this month and
  // the next. Moved a month at a time so past and future both stay reachable.
  const [rangeStart, setRangeStart] = useState(() => monthStartOf(today()));
  const [busy, setBusy] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    start_date: today(), end_date: today(), is_available: false,
    part_day: false, start_time: '09:00', end_time: '12:00', reason: ''
  });

  const asArray = (d, key) =>
    Array.isArray(d) ? d : (d && Array.isArray(d[key]) ? d[key] : []);

  // The last day of the second month.
  const rangeEnd = addDays(addMonths(rangeStart, MONTHS_SHOWN), -1);

  const loadPreview = useCallback(async () => {
    if (!contractorId) return;
    try {
      // This is the call behind "Could not load the calendar for those dates":
      // it asked without the portal token, so the server saw an anonymous
      // caller asking for a named contractor's schedule and refused.
      const res = await axios.get(
        `${API_BASE}/contractors/${contractorId}/availability/effective`,
        { ...auth, params: { from: rangeStart, to: rangeEnd } });
      setPreview(res.data?.days || []);
    } catch (err) {
      console.error('Could not load the availability calendar:', err);
      setError('Could not load the calendar for those dates');
    }
  }, [contractorId, rangeStart, rangeEnd, auth]);

  const load = useCallback(async () => {
    if (!contractorId) return;
    try {
      // Both reads carry the portal token. They did not, while every write
      // beside them did - so a contractor could save a change to their
      // availability and then fail to read it back, which is what "could not
      // load the calendar for those dates" was.
      const [blocksRes, excRes] = await Promise.all([
        axios.get(`${API_BASE}/contractors/${contractorId}/availability`, auth),
        axios.get(`${API_BASE}/contractors/${contractorId}/availability/exceptions`, auth)
      ]);

      const blocks = asArray(blocksRes.data, 'availability_blocks');
      if (blocks.length > 0) {
        setPattern(DAYS.map((_, i) => {
          const b = blocks.find((x) => x.day_of_week === i && x.is_available !== false);
          return b
            ? { day_of_week: i, enabled: true, start_time: hhmm(b.start_time), end_time: hhmm(b.end_time) }
            : { day_of_week: i, enabled: false, start_time: '13:00', end_time: '17:00' };
        }));
      }
      setExceptions(asArray(excRes.data, 'exceptions'));
    } catch (err) {
      console.error('Could not load availability:', err);
      setError('Could not load your availability');
    }
  }, [contractorId, auth]);

  useEffect(() => { load(); }, [load]);
  // Separate effect: moving the calendar refetches only the calendar.
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const flash = (text) => { setMessage(text); setTimeout(() => setMessage(null), 2500); };

  const savePattern = async () => {
    setBusy(true); setError(null);
    try {
      await axios.put(`${API_BASE}/contractors/${contractorId}/availability/pattern`, {
        schedule: pattern.filter((d) => d.enabled).map((d) => ({
          day_of_week: d.day_of_week,
          start_time: d.start_time,
          end_time: d.end_time,
          is_available: true
        }))
      }, auth);
      flash('Standing hours saved');
      await Promise.all([load(), loadPreview()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your standing hours');
    } finally { setBusy(false); }
  };

  const addException = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_BASE}/contractors/${contractorId}/availability/exceptions`, {
        start_date: form.start_date,
        end_date: form.end_date,
        is_available: form.is_available,
        start_time: form.part_day ? form.start_time : null,
        end_time: form.part_day ? form.end_time : null,
        reason: form.reason || null
      }, auth);
      setForm((f) => ({ ...f, reason: '' }));
      flash('Period saved');
      await Promise.all([load(), loadPreview()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that period');
    } finally { setBusy(false); }
  };

  const removeException = async (x) => {
    const from = String(x.start_date).slice(0, 10);
    const to = String(x.end_date).slice(0, 10);
    const dates = to !== from ? `${from} – ${to}` : from;
    if (!(await confirm({ title: `Remove the ${x.is_available ? 'extra cover' : 'time off'} on ${dates}?`,
      message: x.is_available ? 'The usual pattern applies to those days again.' : 'You will be shown as available on those days again.',
      confirmLabel: 'Remove', danger: true }))) return;
    setBusy(true); setError(null);
    try {
      await axios.delete(`${API_BASE}/contractors/${contractorId}/availability/exceptions/${x.id}`, auth);
      await Promise.all([load(), loadPreview()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that period');
    } finally { setBusy(false); }
  };

  const setDay = (i, patch) =>
    setPattern((p) => p.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  // One grid per month. Each starts on its own first day, padded across to
  // its weekday column in a Monday-first row.
  const months = (() => {
    const by = new Map();
    for (const d of preview) {
      const key = monthStartOf(d.date.slice(0, 10));
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(d);
    }
    return [...by.entries()].map(([start, days]) => ({
      start,
      days,
      leadingBlanks: Array.from(
        { length: (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7 }, (_, i) => i)
    }));
  })();

  // "Mon-Fri 13:00-17:00", or the day count when the times differ.
  const patternSummary = (() => {
    const on = pattern.filter((d) => d.enabled);
    if (on.length === 0) return 'None set';
    const sameTimes = on.every(
      (d) => d.start_time === on[0].start_time && d.end_time === on[0].end_time);
    const names = on.map((d) => DAYS[d.day_of_week].slice(0, 3));
    const span = names.length > 2 && on.every((d, i) => i === 0 || d.day_of_week === on[i - 1].day_of_week + 1)
      ? `${names[0]}\u2013${names[names.length - 1]}`
      : names.join(', ');
    return sameTimes ? `${span} ${on[0].start_time}\u2013${on[0].end_time}` : `${on.length} days set`;
  })();

  const isThisMonth = rangeStart === monthStartOf(today());

  return (
    /* Standing hours and the exception list stack on the left; the resolved
       calendar sits beside them on the right, where it stays in view while the
       hours above it are edited. One column below 1100px. */
    <div className="avail-layout">
      {(error || message) && (
        <div className="avail-notices">
          {error && <p className="contractor-error" role="alert">{error}</p>}
          {message && <p className="contractor-ok" role="status">{message}</p>}
        </div>
      )}

      <div className="avail-col">
      {/* ---- standing hours ---- */}
      {/* Collapsed by default: seven day-rows of time inputs pushed the
          calendar off the screen, and the pattern is set once and rarely
          touched. The summary line says what it holds without opening it. */}
      <section className="card">
        <div className="card-header">
          <button
            type="button"
            className="section-toggle"
            aria-expanded={hoursOpen}
            onClick={() => setHoursOpen((v) => !v)}
          >
            <span className={hoursOpen ? 'section-caret is-open' : 'section-caret'} aria-hidden="true">›</span>
            <h3>Standing hours</h3>
            <span className="section-summary">{patternSummary}</span>
          </button>
          {hoursOpen && (
            <button type="button" className="btn btn-primary btn-small" onClick={savePattern} disabled={busy}>
              Save standing hours
            </button>
          )}
        </div>

        {hoursOpen && (
        <>
        <p className="help-text" style={{ marginBottom: '0.875rem' }}>
          The hours you normally work. Holidays and one-off changes go below and
          override this.
        </p>

        <div className="pattern-rows">
          {pattern.map((d, i) => (
            <div key={d.day_of_week} className={d.enabled ? 'pattern-row is-on' : 'pattern-row'}>
              <label className="pattern-day">
                <input
                  type="checkbox"
                  checked={d.enabled}
                  onChange={(e) => setDay(i, { enabled: e.target.checked })}
                />
                <span>{DAYS[d.day_of_week]}</span>
              </label>
              <input
                type="time" value={d.start_time} disabled={!d.enabled}
                onChange={(e) => setDay(i, { start_time: e.target.value })}
                aria-label={`${DAYS[d.day_of_week]} start time`}
              />
              <span className="pattern-sep">to</span>
              <input
                type="time" value={d.end_time} disabled={!d.enabled}
                onChange={(e) => setDay(i, { end_time: e.target.value })}
                aria-label={`${DAYS[d.day_of_week]} end time`}
              />
            </div>
          ))}
        </div>
        </>
        )}
      </section>

      {/* ---- exceptions ---- */}
      <section className="card">
        <h3 style={{ marginBottom: '0.875rem' }}>Availability and extra cover</h3>

        <form className="inline-form" onSubmit={addException}>
          <div className="form-group">
            <label htmlFor="ex-from">From</label>
            <input id="ex-from" type="date" value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </div>
          <div className="form-group">
            <label htmlFor="ex-to">To</label>
            <input id="ex-to" type="date" value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </div>
          <div className="form-group">
            <label htmlFor="ex-kind">Type</label>
            <select id="ex-kind" value={form.is_available ? 'available' : 'off'}
              onChange={(e) => setForm({ ...form, is_available: e.target.value === 'available' })}>
              <option value="off">Unavailable</option>
              <option value="available">Extra cover</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ex-reason">Reason</label>
            <input id="ex-reason" type="text" value={form.reason} placeholder="Annual leave"
              onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>

          <label className="pattern-day" style={{ paddingBottom: '0.5rem' }}>
            <input type="checkbox" checked={form.part_day}
              onChange={(e) => setForm({ ...form, part_day: e.target.checked })} />
            <span>Part of the day only</span>
          </label>

          {form.part_day && (
            <>
              <div className="form-group">
                <label htmlFor="ex-start">Start</label>
                <input id="ex-start" type="time" value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor="ex-end">End</label>
                <input id="ex-end" type="time" value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={busy}>Add period</button>
        </form>

        {exceptions.length === 0 ? (
          <p className="no-data" style={{ marginTop: '1rem' }}>Nothing booked</p>
        ) : (
          <div className="table-wrapper" style={{ marginTop: '1rem' }}>
            <table>
              <thead>
                <tr><th>Dates</th><th>Hours</th><th>Type</th><th>Reason</th><th /></tr>
              </thead>
              <tbody>
                {exceptions.map((x) => (
                  <tr key={x.id}>
                    <td>
                      {String(x.start_date).slice(0, 10)}
                      {String(x.end_date).slice(0, 10) !== String(x.start_date).slice(0, 10) &&
                        ` – ${String(x.end_date).slice(0, 10)}`}
                    </td>
                    <td>{x.start_time ? `${hhmm(x.start_time)} – ${hhmm(x.end_time)}` : 'All day'}</td>
                    <td>
                      <span className={`badge ${x.is_available ? 'badge-resolved' : 'badge-on-hold'}`}>
                        <i />{x.is_available ? 'Extra cover' : 'Unavailable'}
                      </span>
                    </td>
                    <td>{x.reason || '—'}</td>
                    <td>
                      <button type="button" className="btn btn-danger btn-sm"
                        onClick={() => removeException(x)} disabled={busy}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      </div>

      <div className="avail-col avail-col-calendar">
      {/* ---- resolved calendar ---- */}
      {/* A calendar rather than a flat list of day cards: weekday columns and
          week rows, so a pattern of days off reads at a glance. Leading blanks
          line the first day up under its weekday. */}
      <section className="card">
        <div className="cal-head">
          <div className="cal-title">
            <h3>{isThisMonth ? 'This month and next' : 'Two months'}</h3>
            <p className="cal-range">
              {monthLabel(rangeStart)} &ndash; {monthLabel(addMonths(rangeStart, MONTHS_SHOWN - 1))}
            </p>
          </div>
          <div className="cal-nav">
            <button
              type="button" className="btn btn-secondary btn-small"
              onClick={() => setRangeStart((d) => addMonths(d, -1))}
              aria-label="Show the previous month"
            >&lsaquo; Earlier</button>
            <button
              type="button" className="btn btn-secondary btn-small"
              onClick={() => setRangeStart(monthStartOf(today()))}
              disabled={isThisMonth}
            >Today</button>
            <button
              type="button" className="btn btn-secondary btn-small"
              onClick={() => setRangeStart((d) => addMonths(d, 1))}
              aria-label="Show the next month"
            >Later &rsaquo;</button>
          </div>
        </div>

        <div className="cal-key">
          <span><i className="cal-dot is-working" />Working</span>
          <span><i className="cal-dot is-off" />Unavailable</span>
          <span><i className="cal-dot is-extra" />Extra cover</span>
        </div>

        {months.map((m) => (
          <div className="cal-month" key={m.start}>
            <h4 className="cal-month-name">{monthLabel(m.start)}</h4>
            <div className="cal-grid" role="grid" aria-label={`Availability, ${monthLabel(m.start)}`}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                <div key={d} className="cal-weekday" role="columnheader">{d}</div>
              ))}

              {m.leadingBlanks.map((n) => <div key={`pad-${n}`} className="cal-pad" aria-hidden="true" />)}

              {m.days.map((d) => {
                const extra = d.exceptions.some((e) => e.is_available);
                const state = !d.available ? 'is-off' : extra ? 'is-extra' : 'is-working';
                const reasons = d.exceptions.filter((e) => e.reason).map((e) => e.reason);
                // Today needs marking now that the window can sit in the past.
                const isToday = d.date.slice(0, 10) === today();
                return (
                  <div
                    key={d.date}
                    className={`cal-day ${state}${isToday ? ' is-today' : ''}`}
                    aria-current={isToday ? 'date' : undefined}
                    role="gridcell"
                    title={`${d.date}: ${d.available ? 'working' : 'unavailable'}${reasons.length ? ' - ' + reasons.join(', ') : ''}`}
                  >
                    <span className="cal-date">{Number(d.date.slice(8))}</span>
                    {d.windows.length > 0 ? (
                      d.windows.map((w) => (
                        <span key={w.start} className="cal-window">{w.start}&ndash;{w.end}</span>
                      ))
                    ) : (
                      <span className="cal-window cal-muted">&mdash;</span>
                    )}
                    {reasons.map((r, i) => <span key={i} className="cal-reason">{r}</span>)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
      </div>
      {confirmDialog}
    </div>
  );
}
